import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styles from 'modules/Tickets/List/tickets-list.module.scss'
import { Button, DatedList, Grid, Icon, Typography } from 'gu-ui-kit'
import EmptyResults from 'components/EmptyResults'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import { useDispatch, useSelector } from 'react-redux'
import config from 'modules/Tickets/List/config/filters'
import { useTranslation } from 'react-i18next'
import FilterPanel from 'components/Filters/FilterPanel'
import Row from './subcomponents/Row'
import formatRelative from 'utils/dateFns/formatRelative'
import { DROP_LIST, FETCH, PANEL_NAVIGATE, SET_FILTER } from 'modules/Tickets/types'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import GridSpinner from 'components/UI/GridSpinner'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import debounce from 'lodash/debounce'
import useTotallyEmptyList from 'utils/useTotallyEmptyList'
import { Placeholder } from 'modules/Tickets/List/subcomponents/Placeholder'
import omit from 'lodash/omit'

const TicketsList = ({ closeButton }) => {
  const breakpoints = useBreakpoint()
  const dispatch = useDispatch()
  const { t } = useTranslation([])

  const { data, pagination, loaded, loading, loadFailed } = useSelector(({ tickets }) => tickets)

  const filterManager = useFiltersManager({
    filters: useSelector((state) => state['tickets'].filters),
    config: config(),
    setAction: SET_FILTER,
    ignoreQuery: true,
    localStorageKey: 'tickets-list-filters',
  })

  const [page, setPage] = useState(1)

  useEffect(() => {
    setPage(1)
    dispatch({ type: DROP_LIST })
  }, [filterManager.filters])

  const fetchTickets = useCallback(() => {
    !!filterManager.params &&
      dispatch({
        type: FETCH,
        payload: {
          params: {
            ...filterManager.params,
            page,
            limit: pagination.limit,
          },
        },
      })
  }, [filterManager.params, page, pagination])

  useEffect(() => {
    fetchTickets()
  }, [filterManager.params, page])

  const items = useMemo(
    () =>
      data.map((item) => ({
        ...item,
        date: item?.last_message?.created_at,
      })),
    [data],
  )

  const handleScroll = (e) => {
    if (items.length === 0 || items.length >= pagination.total || loading) {
      return
    }

    if (e.target.scrollTop + e.target.clientHeight >= e.target.scrollHeight - 360) {
      setPage((page) => page + 1)
    }
  }

  const debouncedHandleScroll = useCallback(debounce(handleScroll, 250), [items, pagination])

  const [totallyEmptyList, firstLoad] = useTotallyEmptyList(
    omit(filterManager.params, ['statuses']),
    data,
    loaded,
  )

  useEffect(() => {
    if (firstLoad === false && totallyEmptyList) {
      dispatch({
        type: PANEL_NAVIGATE,
        payload: { route: 'new_ticket', params: { id: null } },
      })
    }
  }, [firstLoad, totallyEmptyList])

  if (firstLoad) {
    return <Placeholder />
  }

  if (!firstLoad && totallyEmptyList) {
    return null
  }

  return (
    <Grid
      direction={'column'}
      className={styles['tickets-list__view']}
    >
      <Grid
        gap={32}
        direction={'column'}
      >
        <Grid
          className={styles['tickets-list__header']}
          justifyContent={'space-between'}
          wrap={false}
        >
          <Grid
            alignItems={'center'}
            gap={32}
            direction={'row'}
            wrap={false}
          >
            <Typography
              variant={breakpoints.lg ? 'Header-60' : breakpoints.fb ? 'Header-50' : 'Header-40'}
              component={'h1'}
            >
              {t('tickets::header')}
            </Typography>
            <Button
              onClick={() => {
                dispatch({
                  type: PANEL_NAVIGATE,
                  payload: { route: 'new_ticket', params: { id: null } },
                })
              }}
              variant={'primary'}
              startIcon={<Icon name={'plus'} />}
              size={'s'}
            >
              {t('tickets::new_ticket')}
            </Button>
          </Grid>
          {closeButton}
        </Grid>

        <FilterPanel
          className={clsx(styles['tickets__filter-panel'], 'margin-bottom-32')}
          filterManager={filterManager}
        />
      </Grid>
      <Grid
        className={styles['tickets-list__wrapper']}
        onScroll={debouncedHandleScroll}
      >
        <DatedList
          showMoreButton={false}
          showDates={false}
          emptyComponent={
            <EmptyResults
              className={styles['tickets-list__empty-results']}
              icon={<Icon name={'messages'} />}
            />
          }
          itemComponent={(props) => (
            <Row
              {...props}
              openTicket={(id) =>
                dispatch({
                  type: PANEL_NAVIGATE,
                  payload: { route: 'ticket', params: { id } },
                })
              }
            />
          )}
          pagination={pagination}
          items={items}
          moreButtonLabel={t('common::pagination.show_more')}
          handleGetMoreItems={() => setPage(page + 1)}
          loading={loading}
          loaded={loaded}
          loadFailed={loadFailed}
          l10n={{
            formatFunction: formatRelative,
            today: t('common::dated_list.date_format.today'),
            yesterday: t('common::dated_list.date_format.yesterday'),
            formatDates: {
              short: t('common::dated_list.date_format.short'),
              long: t('common::dated_list.date_format.long'),
            },
          }}
        />
      </Grid>
      {loading && data.length > 0 && (
        <div className={styles['tickets-list__loader']}>
          <GridSpinner width={24} />
        </div>
      )}
    </Grid>
  )
}

export default TicketsList

TicketsList.propTypes = {
  params: PropTypes.object,
  closeButton: PropTypes.element,
}
